import { useEffect, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import ApiService from '../../services/api'
import { useNotification } from '../../context/NotificationContext'

function Folha({ children }) {
  return (
    <main className="prova-publica">
      <div className="prova-publica-sheet">
        <img className="prova-publica-logo" src="/logo.png" alt="Veiga Advogados" />
        {children}
      </div>
    </main>
  )
}

// Prova técnica aberta pelo link direto que o RH envia (/formulario?prova=<código>).
// As questões chegam sem gabarito; a correção das objetivas é feita no servidor.
export default function ProvaTecnicaPublica({ token }) {
  const { showWarning } = useNotification()
  const [prova, setProva] = useState(null)
  const [erro, setErro] = useState(null)
  const [answers, setAnswers] = useState({})
  const [essays, setEssays] = useState({})
  const [enviando, setEnviando] = useState(false)
  const [enviada, setEnviada] = useState(false)
  const [iniciada, setIniciada] = useState(false)
  const [enviadaEm, setEnviadaEm] = useState(null)

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Veiga Advogados - Prova técnica'
    return () => { document.title = previousTitle }
  }, [])

  useEffect(() => {
    ApiService.getProvaTecnicaPublica(token).then((result) => {
      if (!result.success) return setErro(result)
      setProva(result.data)
      setEnviada(result.data?.status === 'enviada')
    })
  }, [token])

  if (erro) {
    const naoEncontrado = erro.status === 404
    return (
      <Folha>
        <h1>{naoEncontrado ? 'Convite não encontrado' : 'Não foi possível abrir a prova'}</h1>
        <p>{naoEncontrado ? 'Este link não está disponível. Confira o endereço recebido ou fale com o RH.' : erro.error}</p>
      </Folha>
    )
  }

  if (!prova) return <Folha><p>Carregando a prova…</p></Folha>

  if (enviada) {
    return (
      <Folha>
        <div className="prova-publica-sucesso" aria-hidden="true"><CheckCircle2 size={44} /></div>
        <span className="prova-publica-kicker">AVALIAÇÃO TÉCNICA CONCLUÍDA</span>
        <h1>Obrigado, {prova.candidato}! Sua prova foi enviada.</h1>
        <p>
          Recebemos todas as suas respostas{enviadaEm ? ` em ${enviadaEm.toLocaleDateString('pt-BR')} às ${enviadaEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}.
          Agradecemos o tempo e a dedicação que você colocou nesta etapa do processo seletivo da Veiga Advogados.
        </p>
        <div className="prova-publica-proximos">
          <h2>Próximos passos</h2>
          <ul>
            <li>Nossa equipe de Recrutamento e Seleção vai analisar suas respostas, objetivas e discursivas, com atenção.</li>
            <li>O retorno sobre a próxima fase será feito pelo e-mail ou telefone que você informou no cadastro.</li>
            <li>Você não precisa fazer mais nada agora e já pode fechar esta página.</li>
          </ul>
        </div>
        <p className="prova-publica-rodape">Este link era individual e não aceita um novo envio. Em caso de dúvida, fale com o RH.</p>
      </Folha>
    )
  }

  const rolarParaOTopo = () => document.querySelector('.prova-publica')?.scrollTo({ top: 0 })

  if (!iniciada) {
    return (
      <Folha>
        <span className="prova-publica-kicker">VEIGA ADVOGADOS · RECRUTAMENTO E SELEÇÃO</span>
        <h1>Boas-vindas, {prova.candidato}!</h1>
        <p>
          Que bom ter você na segunda etapa do nosso processo seletivo. Você avançou na triagem e agora vai fazer
          a <b>{prova.nome}</b>, preparada para conhecermos melhor a forma como você analisa e resolve situações da prática jurídica.
        </p>
        <p>Não existe pegadinha: queremos ver o seu raciocínio. Leia cada questão com calma e responda com sinceridade.</p>

        <div className="prova-publica-fatos">
          <div><strong>{prova.questoes.length}</strong><span>questões objetivas</span></div>
          <div><strong>{prova.discursivas.length}</strong><span>questões discursivas</span></div>
          <div><strong>{prova.duracao} min</strong><span>tempo de referência</span></div>
        </div>

        <h2 className="prova-publica-subtitulo">Antes de começar</h2>
        <ul className="prova-publica-regras">
          <li>Reserve um momento tranquilo, de cerca de {prova.duracao} minutos, para fazer a prova de uma só vez.</li>
          <li>Todas as questões, objetivas e discursivas, precisam ser respondidas.</li>
          <li><b>As respostas só ficam salvas quando você clica em enviar.</b> Se fechar ou recarregar a página antes, elas serão perdidas.</li>
          <li>O envio é feito uma única vez. Revise suas respostas antes de enviar.</li>
        </ul>

        <button className="btn-primary prova-publica-enviar" type="button" onClick={() => { setIniciada(true); rolarParaOTopo() }}>
          Iniciar prova
        </button>
      </Folha>
    )
  }

  const enviar = async () => {
    const faltaObjetiva = prova.questoes.some((q) => answers[q.id] === undefined)
    const faltaDiscursiva = prova.discursivas.some((q) => !(essays[q.id] || '').trim())
    if (faltaObjetiva || faltaDiscursiva) {
      showWarning('Responda todas as questões objetivas e discursivas antes de enviar.')
      return
    }
    if (!window.confirm('Enviar a prova agora? Depois do envio não é possível alterar as respostas.')) return

    setEnviando(true)
    const result = await ApiService.enviarProvaTecnicaPublica(token, { answers, essays })
    setEnviando(false)
    if (result.success || result.status === 409) {
      setEnviadaEm(new Date())
      setEnviada(true)
      rolarParaOTopo()
      return
    }
    // As respostas continuam na tela: o candidato pode tentar de novo.
    showWarning(`${result.error || 'Não foi possível enviar a prova.'} Suas respostas continuam na tela; tente enviar novamente.`)
  }

  return (
    <Folha>
      <span className="prova-publica-kicker">RECRUTAMENTO E SELEÇÃO · ETAPA 2</span>
      <h1>{prova.nome}</h1>
      <p>
        {prova.questoes.length} questões objetivas e {prova.discursivas.length} discursivas · tempo de referência: {prova.duracao} minutos.
        Lembre-se: as respostas só ficam salvas quando você clicar em enviar, no fim da página.
      </p>

      {prova.questoes.map((q, i) => (
        <section className="prova-publica-questao" key={q.id}>
          <span>QUESTÃO {i + 1}{q.topic ? ` · ${q.topic}` : ''}</span>
          <h2>{q.text}</h2>
          {q.options.map((option, j) => (
            <label className={`prova-publica-opcao ${answers[q.id] === j ? 'escolhida' : ''}`.trim()} key={j}>
              <input type="radio" name={q.id} checked={answers[q.id] === j} onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: j }))} />
              {option}
            </label>
          ))}
        </section>
      ))}

      {prova.discursivas.map((q, i) => (
        <section className="prova-publica-questao" key={q.id}>
          <span>DISCURSIVA {i + 1}{q.topic ? ` · ${q.topic}` : ''}</span>
          <h2>{q.text}</h2>
          <textarea
            rows={7}
            value={essays[q.id] || ''}
            onChange={(event) => setEssays((prev) => ({ ...prev, [q.id]: event.target.value }))}
            placeholder="Desenvolva sua resposta..."
          />
        </section>
      ))}

      <button className="btn-primary prova-publica-enviar" type="button" onClick={enviar} disabled={enviando}>
        {enviando ? 'Enviando…' : 'Enviar avaliação técnica'}
      </button>
    </Folha>
  )
}
